import { InternationalizedField, Language } from '@/api/common';
import Hint from '@/api/hint';
import LessonVideoInfo from '@/api/lessonVideoInfo';
import Media from '@/api/media';
import PaddingVideoInfo from '@/api/paddingVideoInfo';
import LockManager from '@/models/lock';
import { isEmpty, isNil } from 'lodash';

export async function lock(slug: string, id: number) {
  const lessonLockManager = new LockManager(slug, id);
  await lessonLockManager.tryAcquireLock();
  return lessonLockManager;
}

export async function safeLock<T>(
  slug: string,
  lockId: number,
  runner: () => Promise<T>
) {
  console.log('lock content', `${slug}-${lockId}`);
  const recordLock = await lock(slug, lockId);
  let id: T = null;
  let error = null;

  try {
    id = await runner();
  } catch (err) {
    error = err;
  }

  console.log('release content', `${slug}-${lockId}`);
  await recordLock.releaseLock();
  console.log('released content', `${slug}-${lockId}`);

  if (error) {
    throw error;
  }

  return id;
}

export function createI18nFields(
  data: { [key: string]: string },
  fieldName: string,
  defaultLang = false
) {
  const field = new InternationalizedField<string>();

  Language.LIST.forEach((lang) => {
    const value = data[`${fieldName}_${lang}`] || '';
    field.set(lang, value);
  });

  if (defaultLang) {
    field.set(Language.LIST[0], data[fieldName] || '');
  }

  return field;
}

export async function createHintField(
  data: { [key: string]: string },
  fieldName: string,
  parent_id: number, 
  content_type: string
) {
  const field_array = new InternationalizedField<Hint[]>();

  for (let i = 0; i < Language.LIST.length; i++) {
    const value = data[`${fieldName}_${Language.LIST[i]}`] || '';
    if (value) {
      const hint = await saveHint(parent_id, content_type, Language.LIST[i], value);
      field_array.set(Language.LIST[i], [hint]);
    }
  }

  return field_array;
}

export async function saveHint(parent_id, content_type, lang, value) {
  const hint = new Hint()
  hint.parent_id = parent_id;
  hint.parent_model = content_type;
  hint.language = lang;
  hint.text = value;
  hint.id = await hint.save();

  return hint;
}


export function createVideoFrameField(
  data: { [key: string]: string },
  fieldName: string
) {
  if (isEmpty(data[fieldName])) return [];

  const videoFrame = data[fieldName];
  const frames = videoFrame.split('>');

  return frames
    .map((item) => {
      const parts = item.split('-');
      if (parts.length < 2) return null;

      const video = new LessonVideoInfo();
      video.start_time = parts[0];
      video.end_time = parts[1];
      return video;
    })
    .filter((item) => !isNil(item));
}

export function createVideoPaddingField(
  data: { [key: string]: string },
  fieldName: string
) {
  if (isEmpty(data[fieldName])) return [];

  const videoFrame = data[fieldName];
  const frames = videoFrame.split('>');

  return frames
    .map((item) => {
      const parts = item.split('-');
      if (parts.length < 2) return null;

      const video = new PaddingVideoInfo();
      video.start_time = parts[0];
      video.end_time = parts[1];
      return video;
    })
    .filter((item) => !isNil(item));
}

export async function convertUrlToFile(
  url: string,
  fileName: string,
  fileType: string
) {
  const headers = new Headers();
  const response = await fetch(url);
  const blob = await response.blob();

  console.log('blob', blob.size, blob.type, blob);

  const file = new File([blob], fileName, {
    type: 'audio/mpeg'
  });
  return file;
}

export async function createMediaField(
  url: string,
  parentId: number,
  parentModel: string,
  fieldName: string,
  type: 'audio' | 'image' = 'audio'
) {
  const file = await convertUrlToFile(
    url,
    type === 'audio' ? `${Date.now()}.mp3` : `${Date.now()}.png`,
    type === 'audio' ? 'audio/mpeg' : 'image/png'
  );
  console.log('file', file.size, file.name, file.type);
  if (type === 'audio') {
    return Media.createAudio(file, parentModel, parentId, fieldName);
  } else {
    console.log(`createImage - ${file}, ${parentModel}, ${parentId}, ${fieldName}`);
    return Media.createImage(file, parentModel, parentId, fieldName);
  }
}
